import { createContext } from 'react';
import {
	TFileId,
	ICreateModifierValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	IUpdateModifier,
	ITotalPrice,
} from 'types';

export type TEditModifierVisibleForm = 'mainForm' | 'translations';
export type TTypeOfEditModifierModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'defaultTranslationIsEmpty';
export interface IInitialFormData {
	selectedCustomer: Nullable<string>;
	totalPrices: ITotalPrice[];
	IDsIsForceDisabledRestaurant: string[];
	selectedModifierGroups: string[];
	name: string;
	description: string;
	externalId: string;
	isDisabledInRestaurants: string[];
	isRestrictedInRestaurants: string[];
	isPreselectedInRestaurants: string[];
	quantityRegulationRestaurants: string[];
	quantityMaxLimit: number | null;
	activationSchedule: string | null;
	deactivationSchedule: string | null;
	filePath: Nullable<string>;
	translations: IMenuTranslation[];
}

export interface IEditModifierState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	visibleForm: TEditModifierVisibleForm;
	modifierId: string;
	filePath: Nullable<string>;
	preSignedUrl: Nullable<string>;
	fileId: TFileId;
	selectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	restaurantsForPrice: string[];
	price: string;
	totalPrices: ITotalPrice[];
	IDsIsForceDisabledRestaurant: string[];
	fetchedModifierGroups: IItemForGetShortInfo[];
	selectedModifierGroups: string[];
	name: string;
	articleNumber: Nullable<string>;
	description: string;
	externalId: string;
	isDisabledInRestaurants: string[];
	isRestrictedInRestaurants: string[];
	isPreselectedInRestaurants: string[];
	quantityRegulationRestaurants: string[];
	quantityMaxLimit: number | null;
	defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IMenuTranslation[];
	activationSchedule: string | null;
	deactivationSchedule: string | null;
	dataForSubmit: IUpdateModifier | null;
	typeOfModalWindow: TTypeOfEditModifierModalWindow;
	errorResponseData: IServerError<ICreateModifierValidationError> | null;
}

export const editModifierInitialState: IEditModifierState = {
	needRefetchData: false,
	initialFormData: null,
	visibleForm: 'mainForm',
	modifierId: '',
	filePath: null,
	preSignedUrl: null,
	fileId: null,
	selectedCustomer: null,
	fetchedRestaurants: [],
	restaurantsForPrice: [],
	price: '',
	totalPrices: [],
	IDsIsForceDisabledRestaurant: [],
	fetchedModifierGroups: [],
	selectedModifierGroups: [],
	name: '',
	articleNumber: null,
	description: '',
	externalId: '',
	isDisabledInRestaurants: [],
	isRestrictedInRestaurants: [],
	isPreselectedInRestaurants: [],
	quantityRegulationRestaurants: [],
	quantityMaxLimit: null,
	defaultCustomerLanguage: null,
	selectedTranslationTab: null,
	translations: [],
	activationSchedule: null,
	deactivationSchedule: null,
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface IEditModifierContext extends IEditModifierState {
	refetchData(value: boolean): void;
	changeVisibleForm(value: TEditModifierVisibleForm): void;
	setFileId(value: TFileId): void;
	deletePhoto(): void;
	setCustomer(value: Nullable<string>): void;
	setFetchedModifierGroups(value: IItemForGetShortInfo[]): void;
	setModifierGroups(value: string[]): void;
	updateModifierGroups(value: IItemForGetShortInfo[]): void;
	changeName(value: string): void;
	changeExternalId(value: string): void;
	generateUniqueExternalId(): void;
	changeDescription(value: string): void;
	setFetchedRestaurants(value: IItemForGetShortInfoWithStatus[]): void;
	setRestaurantsForPrice(value: string[]): void;
	changePrice(value: string): void;
	addPrices(value: ITotalPrice[]): void;
	removePrice(value: string[]): void;
	setIDsIsForceDisabledRestaurant(value: string): void;
	setDisabledInRestaurants(value: string[]): void;
	setRestrictedInRestaurants(value: string[]): void;
	setPreselectedInRestaurants(value: string[]): void;
	setQuantityRegulationRestaurants(value: string[]): void;
	changeQuantityMaxLimit(value: number): void;
	setDefaultCustomerLanguage(value: Nullable<IDefaultLanguage>): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	changeDescriptionTranslation(languageId: TLanguageIDs, value: string): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setDataForSubmit(value: IUpdateModifier | null): void;
	changeTypeOfModalWindow(value: TTypeOfEditModifierModalWindow): void;
	showErrorModalWithInfo(errorResponseData: IServerError<ICreateModifierValidationError> | null): void;
	resetFields(): void;
}

export const EditModifierContext = createContext<IEditModifierContext>({
	refetchData: () => undefined,
	changeVisibleForm: () => undefined,
	setFileId: () => undefined,
	deletePhoto: () => undefined,
	setCustomer: () => undefined,
	setFetchedModifierGroups: () => undefined,
	setModifierGroups: () => undefined,
	updateModifierGroups: () => undefined,
	changeName: () => undefined,
	changeExternalId: () => undefined,
	generateUniqueExternalId: () => undefined,
	changeDescription: () => undefined,
	setFetchedRestaurants: () => undefined,
	setRestaurantsForPrice: () => undefined,
	changePrice: () => undefined,
	addPrices: () => undefined,
	removePrice: () => undefined,
	setIDsIsForceDisabledRestaurant: () => undefined,
	setDisabledInRestaurants: () => undefined,
	setRestrictedInRestaurants: () => undefined,
	setPreselectedInRestaurants: () => undefined,
	setQuantityRegulationRestaurants: () => undefined,
	changeQuantityMaxLimit: () => undefined,
	setDefaultCustomerLanguage: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	changeDescriptionTranslation: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...editModifierInitialState,
});
